import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import { InputAdornment, SvgIcon, Tooltip } from '@material-ui/core';
//Icons
import * as MuiIcons from '@material-ui/icons';
import IconSearch from '@material-ui/icons/Search';
import IconAddCircle from '@material-ui/icons/AddCircle';

import { SHOW_SHORTCUTS, TIPO_DOCUMENTO } from '~/constants';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';

import OrcamentoTable from '../../components/OrcamentoTable';
import { OrcamentoOnlineService } from '~/store/modules/orcamento/orcamento.service';
import { getFilterByData } from '~/store/modules/monitor/monitor-helper';
import { ordenarPorRelevancia, TIPO_PESQUISA } from '~/utils/weighted-search';

const Orcamento = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const history = useHistory();

  const isOnline = useSelector(getApplicationIsOnline());

  const anchorRef = useRef();
  const { terminal } = useSelector(state => state.app);
  const { loading, filtroData, filtroDataOptions, page, rowsPerPage } = useSelector(
    state => state.monitor
  );

  const handleSearch = async (goBackToFirstPage) => {
      try {
        dispatch(monitorActions.setLoading(true));

        let conditions = []
        let orderby = ''

        let tipoPesquisa = null
        if (/^\d+$/.test(searchTerm)) { // números
          tipoPesquisa = TIPO_PESQUISA.NUMEROS_INTEIROS
          orderby = `numero asc, valorTotal asc`
          conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
        }
        else if (/^(?=.*\d)(?=.*[.,])[\d.,]+$/.test(searchTerm)) { // numeros + ponto ou virgula
          tipoPesquisa = TIPO_PESQUISA.NUMEROS_FLUTUANTES
          orderby = `valorTotal asc`
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm.replace(/,/g, ".")}')`)
        }
        else if (/^[\p{L}\s]+$/u.test(searchTerm)) { // texto
          tipoPesquisa = TIPO_PESQUISA.TEXTO
          orderby = `clienteNome asc, fatVendedor/nome asc`
          conditions.push(`contains(clienteNome,'${searchTerm}')`)
          conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
        }
        else { // misto
          tipoPesquisa = TIPO_PESQUISA.MISTO
          orderby = `numero asc, clienteNome asc, fatVendedor/nome asc, valorTotal asc`
          conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(clienteNome,'${searchTerm}')`)
          conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
        }

        let filter = [conditions.length ? `(${conditions.join(' or ')})` : '']
        filter.push(...getFilterByData(filtroData, 'dataEmissao'))

        let newPage = goBackToFirstPage ? 0 : page

        const service = new OrcamentoOnlineService();
        const res = await service.getAll({
          skip: newPage === 0 ? 0 : newPage * rowsPerPage,
          top: rowsPerPage,
          filter: filter,
          orderby: orderby,
        });

        if (goBackToFirstPage) {
          dispatch(monitorActions.setPage(0));
        }

        const items = ordenarPorRelevancia(res.data.items, searchTerm, TIPO_DOCUMENTO.ORCAMENTO, tipoPesquisa)

        dispatch(monitorActions.loadOrcamentosSearchSuccess({
          orcamentos: items,
          valorTotalDocumentos: items.reduce((total, item) => total + item.valorTotal, 0),
          quantidadeDocumentos: res.data.count ?? items.length
        }))

      } catch (err) {
        throw err;
      } finally {
        dispatch(monitorActions.setLoading(false));
      }
  };

  useEffect(() => {
    if(searchTerm) {
      handleSearch(false)
    } 
    else {
      dispatch(monitorActions.loadOrcamentosRequest());
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    if(searchTerm) {
      handleSearch(true)
    } 
    else {
      dispatch(monitorActions.setPage(0));
      dispatch(monitorActions.loadOrcamentosRequest());
    }
  }, [filtroData]);

  useEffect(() => {
    return () => {
      dispatch(monitorActions.limparDados());
    };
  }, []);

  // useEffect(() => {
  //   if(searchTerm) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 1000)
  
  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  return (
    <>
      <Box p={2} display="flex" justifyContent="space-between">
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginRight: '16px', flex: '0 1 auto' }}>
          <Button
            variant="contained"
            className='button-warning'
            style={{ height: 44, whiteSpace: 'normal' }}
            disabled={terminal === null}
            onClick={() => {
              history.push('/pdv', {
                tipoDocumento: TIPO_DOCUMENTO.ORCAMENTO
              });
            }}
          >
            <span variant="span" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MuiIcons.AddCircle style={{position: 'relative', bottom: '2px'}}/>
              <span style={{ verticalAlign: 'middle' }}>Novo Orçamento {SHOW_SHORTCUTS && '(CTRL+O)'}</span>
            </span>
          </Button>
        </Box>

        <Box display="flex" alignItems={'flex-start'} style={{ flex: '1 1 auto' }}>
          <Tooltip title="Buscar por nº do documento, cliente, vendedor ou valor...">
            <Box display="inline-block" style={{flex: '1 0 auto'}}>
              <TextField
                disabled={loading}
                style={{width: '100%'}}
                InputProps={{
                  style: {
                    height: 44
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon fontSize="small" color="action">
                        <MuiIcons.Search />
                      </SvgIcon>
                    </InputAdornment>
                  )
                }}
                placeholder="Pesquisar orçamento..."
                variant="outlined"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    if (searchTerm.trim() === '') {
                      setSearchTerm('')
                      dispatch(monitorActions.setPage(0));
                      dispatch(monitorActions.loadOrcamentosRequest());
                    } else {
                      handleSearch(true);
                    }
                  }
                }}
              />
            </Box>
          </Tooltip>

          <ButtonGroup
            variant="contained"
            color="default"
            ref={anchorRef}
            aria-label="split button"
            style={{ marginLeft: 16, minHeight: 44, flex: '0 1 auto' }}
          >
            {filtroDataOptions.map((f, index) => (
              <Button
                key={`orcamento-filtro-data-${index}`}
                color={f.filtro === filtroData ? 'primary' : 'default'}
                style={{ whiteSpace: 'normal' }}
                className={f.filtro === filtroData ? 'button-primary' : 'button-white'}
                onClick={() => dispatch(monitorActions.setFiltroData(f.filtro))}
              >
                {f.titulo}
              </Button>
            ))}
          </ButtonGroup>
          {/* <Button
            variant="contained"
            style={{ height: 44, marginLeft: 16 }}
            disabled={terminal === null}
            onClick={() => dispatch(monitorActions.loadOrcamentosRequest(new Date().getTime()))}
          >
            <MuiIcons.Refresh />
          </Button> */}
        </Box>
      </Box>
      <Divider />
      <OrcamentoTable />
    </>
  );
};

export default Orcamento;
